import * as React from 'react';
import classNames from 'classnames';

import './styles.scss';
import { compose } from 'redux';
import withStyles, { WithStyles } from 'react-jss';

export interface IControlCheckboxProps {
    /** Controlled value */
    checked: boolean;
    /** Disabled status */
    disabled?: boolean;
    /** Focus handler */
    onFocus?: (event) => void;
    /** Blur handler */
    onBlur?: (event) => void;
    /** Click handler */
    onClick?: (event) => void;
    /** Change handler */
    onChange?: (event) => void;
    /** Focus field on mount */
    autoFocus?: boolean;
    /** Error */
    hasError?: boolean;
    /** Checkbox wiggle on error */
    errorAnimated?: boolean;
    /** className to append */
    className?: string;
    /** DOM target element name attribute */
    name?: string;
    /** DOM target element id attribute */
    id?: string;
    /** Element to use as wrapper instead of label */
    wrapperElement?: 'span' | 'label' | 'div';
    /** Label for accessibility */
    label?: string;
    /** Value */
    value?: string;
    /** Data test id for automated tests */
    testId?: string;
}

interface IState {
}

type Props = IControlCheckboxProps & WithStyles<any>;

/**
 * HTML checkbox replacement with extended functionality and styling.
 */
class ControlCheckbox extends React.Component<Props, IState> {
    static displayName: string = 'ControlCheckbox';

    private didFocus: boolean = false;
    private innerRef: HTMLInputElement | null = null;

    /**
     * Focuses checkbox element
     * @public
     */
    focus = () => {
        if (this.innerRef) {
            this.innerRef.focus();
        }
    };

    /**
     * Blurs checkbox element
     * @public
     */
    blur = () => {
        if (this.innerRef) {
            this.innerRef.focus();
            this.innerRef.blur();
        }
    };

    /**
     * Clicks checkbox element
     * @public
     */
    click = () => {
        if (this.innerRef) {
            this.innerRef.click();
        }
    };

    render () {
        const Elem = !!this.props.wrapperElement ? this.props.wrapperElement : 'label';

        return (
            <Elem className={classNames('control-checkbox', this.props.classes.main, this.props.className)}>
                <span className={classNames('control-checkbox-overflow', {
                    'has-error': this.props.hasError,
                    'error-animated': this.props.errorAnimated,
                })}
                >
                    <input
                        type="checkbox"
                        checked={this.props.checked}
                        disabled={this.props.disabled}
                        onFocus={this.props.onFocus}
                        onBlur={this.props.onBlur}
                        onClick={this.props.onClick}
                        onChange={this.props.onChange}
                        aria-label={this.props.label}
                        value={this.props.value}
                        ref={(node) => {
                            this.innerRef = node;
                            if (node && this.props.autoFocus && !this.didFocus) {
                                node.focus();
                                this.didFocus = true;
                            }
                        }}
                        name={this.props.name}
                        id={this.props.id}
                        data-testid={this.props.testId}
                    />
                    <span className={classNames('control-checkbox-checkbox', this.props.classes.checkbox, {
                        active: this.props.checked,
                        disabled: this.props.disabled,
                    })}>
                        <span/>
                    </span>
                </span>
            </Elem>
        );
    }
}

const styles = (theme) => {
    return {
        main: {
            '&.layout-default': {},
        },
        checkbox: {
            '&.active': {
                backgroundColor: [[theme.colors.primary], '!important'],
            },
        },
    };
};

export default compose<React.ComponentClass<IControlCheckboxProps>>(
    // @ts-ignore
    withStyles(styles, { injectTheme: true })
)(ControlCheckbox);
